import { useEffect, useMemo, useState } from "react";
import { Search, Send, Users } from "lucide-react";
import { Button } from "components/ui/Button";
import { Input } from "components/ui/Input";
import { Select } from "components/ui/Select";
import { EmailingOverview, EmailingUser, RecipientRef, UserSegment, getAllEmailingUsers, getEmailingUsers } from "interfaces/emailing";
import { LastEmailCell, OfferBadge, Pager, SEGMENTS, SelectionBar, since } from "./parts";

const SORTS = [
    { value: "createdAt", label: "Inscription récente" },
    { value: "lastConnection", label: "Connexion récente" },
    { value: "trialEndsAt", label: "Fin d'essai proche" },
];

const ref = (u: EmailingUser): RecipientRef => ({ kind: "user", _id: u._id, email: u.email, name: u.name });

/**
 * App users as recipients: segment chips (who to talk to now), search, how many emails each one
 * got and the last one, a selection kept across pages, and « Envoyer un email ».
 */
export default function Recipients({
    overview,
    segment,
    setSegment,
    refreshKey,
    onCompose,
    onOpen,
}: {
    overview: EmailingOverview | null;
    segment: UserSegment;
    setSegment: (s: UserSegment) => void;
    refreshKey: number;
    onCompose: (recipients: RecipientRef[]) => void;
    onOpen: (r: RecipientRef) => void;
}) {
    const [q, setQ] = useState("");
    const [sort, setSort] = useState("createdAt");
    const [page, setPage] = useState(1);
    const [data, setData] = useState<{ users: EmailingUser[]; total: number; totalPages: number } | null>(null);
    const [selected, setSelected] = useState<Map<string, RecipientRef>>(new Map());
    const [selectingAll, setSelectingAll] = useState(false);
    const [truncated, setTruncated] = useState(false);

    useEffect(() => setPage(1), [q, segment, sort]);
    useEffect(() => {
        const t = setTimeout(() => {
            getEmailingUsers({ segment, q: q || undefined, sort, page, limit: 25 })
                .then(setData)
                .catch(() => setData({ users: [], total: 0, totalPages: 0 }));
        }, 250);
        return () => clearTimeout(t);
    }, [q, segment, sort, page, refreshKey]);

    const rows = data?.users ?? [];
    const allOnPage = rows.length > 0 && rows.every((u) => selected.has(u._id));

    const toggle = (u: EmailingUser) =>
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(u._id)) next.delete(u._id);
            else next.set(u._id, ref(u));
            return next;
        });

    const togglePage = () =>
        setSelected((prev) => {
            const next = new Map(prev);
            rows.forEach((u) => (allOnPage ? next.delete(u._id) : next.set(u._id, ref(u))));
            return next;
        });

    const selectAll = async () => {
        setSelectingAll(true);
        try {
            const res = await getAllEmailingUsers({ segment, q: q || undefined });
            setSelected((prev) => {
                const next = new Map(prev);
                res.users.forEach((u) => next.set(u._id, u));
                return next;
            });
            setTruncated(res.truncated);
        } finally {
            setSelectingAll(false);
        }
    };

    const selection = useMemo(() => [...selected.values()], [selected]);

    return (
        <div className="space-y-4 pb-20">
            <div className="flex flex-wrap gap-2">
                {SEGMENTS.map((s) => {
                    const active = segment === s.id;
                    const count = overview?.users[s.id];
                    return (
                        <button
                            key={s.id}
                            onClick={() => setSegment(s.id)}
                            title={s.hint}
                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                active ? "border-ink bg-ink text-ink-inverse" : "border-border bg-surface text-ink hover:bg-surface-sunken"
                            }`}
                        >
                            {s.label}
                            {count !== undefined && <span className={`text-xs ${active ? "text-ink-inverse-muted" : "text-ink-faint"}`}>{count}</span>}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-ink-faint" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, email, entreprise…" className="pl-9" />
                </div>
                <Select value={sort} onChange={setSort} options={SORTS} />
                {data && (
                    <button onClick={selectAll} disabled={selectingAll || data.total === 0} className="ml-auto text-sm text-ink-muted underline hover:text-ink disabled:opacity-40">
                        {selectingAll ? "Sélection…" : `Sélectionner les ${data.total} de ce filtre`}
                    </button>
                )}
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-surface">
                {!data ? (
                    <p className="py-16 text-center text-sm text-ink-faint">Chargement…</p>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-16 text-sm text-ink-muted">
                        <Users className="h-5 w-5" />
                        Aucun utilisateur
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-surface-sunken text-xs font-semibold uppercase tracking-wide text-ink-faint">
                                <tr>
                                    <th className="w-10 px-4 py-3">
                                        <input type="checkbox" checked={allOnPage} onChange={togglePage} aria-label="Sélectionner la page" className="rounded-sm accent-accent" />
                                    </th>
                                    <th className="px-4 py-3">Utilisateur</th>
                                    <th className="px-4 py-3">Offre</th>
                                    <th className="px-4 py-3">Connexion</th>
                                    <th className="px-4 py-3 text-right">Emails envoyés</th>
                                    <th className="px-4 py-3">Dernier email envoyé</th>
                                    <th className="w-16 px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((u) => (
                                    <tr key={u._id} className={`border-t border-border transition-colors hover:bg-surface-sunken/60 ${selected.has(u._id) ? "bg-accent-subtle/40" : ""}`}>
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selected.has(u._id)} onChange={() => toggle(u)} aria-label={`Sélectionner ${u.email}`} className="rounded-sm accent-accent" />
                                        </td>
                                        <td className="max-w-xs cursor-pointer px-4 py-3" onClick={() => onOpen(ref(u))}>
                                            <p className="truncate font-medium text-ink">{u.name || u.email}</p>
                                            <p className="truncate text-xs text-ink-muted">
                                                {u.name ? u.email : ""}
                                                {u.company ? `${u.name ? " · " : ""}${u.company}` : ""}
                                            </p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col items-start gap-1">
                                                <OfferBadge user={u} />
                                                {u.marketingOptOut && <span className="text-xs text-ink-faint">désinscrit marketing</span>}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-3 text-ink-muted">{since(u.lastConnection)}</td>
                                        <td className="cursor-pointer px-4 py-3 text-right font-medium text-ink" onClick={() => onOpen(ref(u))}>
                                            {u.emails.count}
                                        </td>
                                        <td className="max-w-xs cursor-pointer px-4 py-3" onClick={() => onOpen(ref(u))}>
                                            <LastEmailCell emails={u.emails} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button size="sm" variant="ghost" onClick={() => onCompose([ref(u)])} title="Envoyer un email" aria-label={`Envoyer un email à ${u.email}`}>
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {data && <Pager page={page} totalPages={data.totalPages} setPage={setPage} />}

            <SelectionBar
                count={selection.length}
                note={truncated ? "(limité à 500)" : undefined}
                onClear={() => {
                    setSelected(new Map());
                    setTruncated(false);
                }}
                onSend={() => onCompose(selection)}
            />
        </div>
    );
}
